import {
  Box,
  Button,
  Flex,
  FormErrorMessage,
  Link,
  Text,
  Textarea,
  VStack,
  useColorModeValue,
  Avatar,
  HStack,
  IconButton,
  Heading,
} from '@chakra-ui/react';
import { ChevronLeftIcon } from '@chakra-ui/icons';
import React, { ReactElement, useEffect } from 'react';
import { Link as RouterLink, useParams } from 'react-router-dom';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useMutation, useQuery } from '@apollo/client';
import { formatDatetimeWithDay } from '~/utilities/formatDatetime';
import {
  acceptScout,
  messageThread,
  messageThreads,
  postMessage,
} from '~/queries';

import { UserAvatar, FormControl } from '~/components/molecules';
import { useUser } from '~/containers';
import {
  Message,
  MessageKind,
  MessageThread,
  MessageThreadOrigin,
  MessageThreads,
} from '~/generated';

const cardAlign = (messageKind: MessageKind) => {
  switch (messageKind) {
    case MessageKind.FromCompany:
      return 'flex-start';
    case MessageKind.FromSeeker:
      return 'flex-end';
    default:
      return 'center';
  }
};

const authorUser = ({ message }: { message: Message }) => {
  if (message.kind === MessageKind.FromSeeker) {
    const { jobSeeker } = message;

    return `${jobSeeker?.fullName || ''}`;
  }
  if (message.kind === MessageKind.FromCompany) {
    const { companyStaff: staff } = message;

    return `${staff?.fullName || ''}`;
  }

  return '';
};

type MessageCardProps = {
  message: Message;
  showScout: boolean;
  thread?: MessageThread;
};

const MessageCard = (props: MessageCardProps) => {
  const { message, showScout, thread } = props;
  const { job, company } = thread || {};
  const { text, kind, createdAt } = message;
  const { user, image } = useUser();

  const cardBackground =
    kind === MessageKind.FromSeeker
      ? ['#F9FAFB', 'gray.600']
      : ['white', 'gray.800'];

  return (
    <Flex p={{ base: 1, md: 50 }} w="full" justifyContent={cardAlign(kind)}>
      <Flex
        direction="column"
        px={8}
        py={4}
        rounded="lg"
        shadow="lg"
        bg={useColorModeValue(cardBackground[0], cardBackground[1])}
        w="90%"
      >
        <Flex justifyContent="space-between" alignItems="center">
          <Flex justifyContent="space-between" alignItems="center">
            {kind === MessageKind.FromSeeker ? (
              <UserAvatar
                name={user?.attributes?.family_name}
                imageKey={image}
                size="md"
              />
            ) : (
              <Avatar src={company?.logo ?? ''} size="md" maxW="45px" />
            )}

            <Text
              color={useColorModeValue('gray.700', 'gray.200')}
              fontWeight="700"
              paddingLeft="5px"
            >
              {authorUser({ message })}
            </Text>
          </Flex>
          <Text
            fontSize="sm"
            color={useColorModeValue('gray.600', 'gray.400')}
            paddingLeft="5px"
          >
            {formatDatetimeWithDay(createdAt)}
          </Text>
        </Flex>

        <Box mt={2}>
          <Text mt={2} color={useColorModeValue('gray.600', 'gray.300')}>
            {text}
          </Text>
          {showScout && job?.id && (
            <Button marginTop="20px">
              <Link
                as={RouterLink}
                to={`/jobs/${job.id}`}
                // bg={threadId === thread.id ? 'lightblue' : 'white'}
                textDecoration="none"
                _hover={{ bg: 'lightgray' }}
                target="_blank"
              >
                求人情報
              </Link>
            </Button>
          )}
        </Box>
      </Flex>
    </Flex>
  );
};

MessageCard.defaultProps = {
  thread: undefined,
};

type MessageInput = {
  text: string;
};

const Messaging = (): ReactElement => {
  const { threadId } = useParams<{ threadId: string | undefined }>();

  const { data: threads } = useQuery<{ messageThreads: MessageThreads }>(
    messageThreads,
  );
  const [performPostMessage, { error }] = useMutation(postMessage);
  const [performAcceptScout] = useMutation(acceptScout);
  const { data: messages, refetch } = useQuery<{
    messageThread: MessageThread;
  }>(messageThread, {
    variables: { id: threadId },
    skip: !threadId,
  });
  const isScout = messages?.messageThread?.origin === MessageThreadOrigin.Scout;
  const canPostMessage = messages?.messageThread?.canPostMessage;

  useEffect(() => {
    if (threadId) {
      void refetch({
        variables: { id: threadId || threads?.messageThreads.data[0]?.id },
      });
    }
  }, [threadId, refetch, threads]);

  const {
    register,
    handleSubmit,
    reset,
    formState,
    formState: { errors },
  } = useForm<MessageInput>();
  const onSubmit: SubmitHandler<MessageInput> = async (data) => {
    await performPostMessage({ variables: { threadId, text: data.text } });
    if (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } else {
      void refetch();
      reset();
    }
  };
  const handleAcceptScout = async () => {
    await performAcceptScout({ variables: { threadId } });
    if (error) {
      // eslint-disable-next-line no-console
      console.error(error);
    } else {
      void refetch();
    }
  };

  return (
    <Flex h="calc(100vh - 64px)">
      <Box
        sx={{
          borderRight: 'solid 1px lightgray',
          minW: '350px',
        }}
        width={{ base: '100%', md: '400px' }}
        display={{ base: threadId ? 'none' : 'block', md: 'block' }}
      >
        <VStack paddingLeft="3px" alignItems="stretch" spacing={0}>
          {threads?.messageThreads.data.map((thread) => (
            <Link
              as={RouterLink}
              to={`/messages/${thread.id}`}
              bg={threadId === thread.id ? 'lightblue' : 'white'}
              textDecoration="none"
              _hover={{ bg: 'lightgray' }}
              key={thread.id}
            >
              <Box
                borderBottom="solid 1px lightgray"
                py="20px"
                paddingLeft="10px"
              >
                <HStack>
                  <Avatar src={thread.company?.logo ?? ''} size="md" />
                  <VStack align="left" sx={{ px: 3 }}>
                    <Heading size="sm">{thread.company.name}</Heading>
                    <Text
                      overflow="hidden"
                      display="-webkit-box"
                      sx={{
                        wordBreak: 'break-word',
                        overflow: 'hidden',
                        display: '-webkit-box',
                        WebkitLineClamp: '2',
                        WebkitBoxOrient: 'vertical',
                        textOverflow: 'ellipsis',
                        fontSize: 'sm',
                        pr: '10px',
                      }}
                    >
                      {thread.latestMessage?.text}
                    </Text>
                    {thread.latestMessage && (
                      <Text color="grey" fontSize="xs">
                        {formatDatetimeWithDay(thread.latestMessage?.createdAt)}
                      </Text>
                    )}
                  </VStack>
                </HStack>
              </Box>
            </Link>
          ))}
        </VStack>
      </Box>
      <Flex
        bg="gray.50"
        direction="column"
        sx={{ flexGrow: 1 }}
        display={{ base: threadId ? 'flex' : 'none', md: 'flex' }}
      >
        <Box borderBottom="solid 1px lightgray" overflowY="scroll">
          <VStack
            style={{ whiteSpace: 'pre-line' }}
            alignItems="stretch"
            paddingLeft="10px"
            marginY="80px"
            flexGrow="1"
            spacing={5}
          >
            {messages?.messageThread?.messages?.map((message, idx) => (
              <MessageCard
                key={message.id}
                {...{ message }}
                showScout={isScout && idx === 0}
                thread={messages?.messageThread}
              />
            ))}
            {messages?.messageThread.canAcceptScout && (
              <Box paddingLeft="50px">
                <Button maxWidth="fit-content" onClick={handleAcceptScout}>
                  応募する
                </Button>
                <Text color="gray.600" fontSize="sm">
                  ※「応募する」を押すことで企業とメッセージのやり取りができます。
                </Text>
              </Box>
            )}
          </VStack>
        </Box>

        <Box padding="30px">
          <form onSubmit={handleSubmit(onSubmit)}>
            <FormControl isInvalid={!!errors.text}>
              <Textarea
                placeholder={`${
                  messages?.messageThread?.company?.name || ''
                }とのメッセージ`}
                disabled={!canPostMessage}
                // eslint-disable-next-line react/jsx-props-no-spreading
                {...register('text', { required: true })}
              />
              <FormErrorMessage>{errors.text?.message}</FormErrorMessage>
            </FormControl>
            <Button
              colorScheme="blue"
              isLoading={formState.isSubmitting}
              type="submit"
              disabled={!canPostMessage}
            >
              送信
            </Button>
          </form>
        </Box>

        <Link
          as={RouterLink}
          to="/messages"
          textDecoration="none"
          _hover={{ bg: 'lightgray' }}
          sx={{ position: 'fixed', top: '80px', left: '10px' }}
          display={{ base: 'block', md: 'none' }}
        >
          <IconButton
            colorScheme="blue"
            aria-label="Search database"
            fontSize="2xl"
            icon={<ChevronLeftIcon />}
          />
        </Link>
      </Flex>
    </Flex>
  );
};

export default Messaging;
